import { FC, useCallback, useState } from 'react';

import useErrorHandledMutation, {
  throwOnlyUnhandledErrors,
} from 'common/hooks/useErrorHandledMutation';
import useToast from 'components/ToastProvider/useToast';
import useSendCompanyInvitation from 'features/Onboarding/common/useSendCompanyInvitation';

import {
  beneficiariesDocsStatusQuery,
  updateCompanyInvitationEmailAddressMutation,
} from '../graphql';
import locales from '../locales';
import {
  BeneficiaryCardProps,
  UpdateCompanyInvitationEmailVariables,
} from '../types';
import Component from './component';
import { BeneficiaryCardFormSchemaValid } from './formSchema';

const BeneficiaryCard: FC<BeneficiaryCardProps> = ({
  beneficiary,
  companyProfileId,
}) => {
  // Early checks
  if (beneficiary.invitation === null) {
    throw new Error('Beneficiary unexpectedly missing the invitation field');
  }
  if (companyProfileId === undefined) {
    throw new Error('companyProfileId should be defined');
  }
  if (beneficiary.invitation.companyInvitationId === undefined) {
    throw new Error('companyInvitationId should be defined');
  }

  const { firstName, invitation, lastName } = beneficiary;

  const { showToast } = useToast();
  const [isOpen, setOpen] = useState(false);

  const { companyInvitationId } = invitation;

  const {
    loading: resendInvitationLoading,
    sendCompanyInvitation: sendCompanyInvitationMutation,
  } = useSendCompanyInvitation({
    awaitRefetchQueries: true,
    refetchQueries: () => [
      {
        query: beneficiariesDocsStatusQuery,
        variables: {
          companyProfileId,
        },
      },
    ],
  });

  const sendCompanyInvitation = useCallback(async () => {
    await sendCompanyInvitationMutation({
      companyInvitationId,
      companyProfileId,
    });

    showToast({
      message: `${locales.formatString(locales.linkResendInvitationSuccess, {
        name: `${firstName} ${lastName}`,
      })}`,
      type: 'success',
    });
  }, [
    sendCompanyInvitationMutation,
    companyInvitationId,
    companyProfileId,
    showToast,
    firstName,
    lastName,
  ]);

  const [updateInvitationEmailMutator, { loading: isUpdatingInvitationEmail }] =
    useErrorHandledMutation<never, UpdateCompanyInvitationEmailVariables>(
      updateCompanyInvitationEmailAddressMutation,
    );

  const updateInvitationEmail = useCallback(
    async (formValues: BeneficiaryCardFormSchemaValid) => {
      await updateInvitationEmailMutator({
        awaitRefetchQueries: true,
        refetchQueries: () => [
          {
            query: beneficiariesDocsStatusQuery,
            variables: {
              companyProfileId,
            },
          },
        ],
        variables: {
          input: {
            companyInvitationId,
            companyProfileId,
            inviteeEmail: formValues.email,
          },
        },
      }).catch(throwOnlyUnhandledErrors);

      await sendCompanyInvitationMutation({
        companyInvitationId,
        companyProfileId,
      });
      setOpen(false);
    },
    [
      companyInvitationId,
      companyProfileId,
      sendCompanyInvitationMutation,
      updateInvitationEmailMutator,
    ],
  );

  return (
    <Component
      beneficiary={beneficiary}
      companyProfileId={companyProfileId}
      isOpen={isOpen}
      loading={isUpdatingInvitationEmail || resendInvitationLoading}
      onClose={() => {
        setOpen(false);
      }}
      onOpen={() => {
        setOpen(true);
      }}
      onSubmit={updateInvitationEmail}
      sendInvitation={sendCompanyInvitation}
    />
  );
};

export default BeneficiaryCard;
